import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import LocationOptions, { useAllLocations } from '../components/LocationOptions';

const STAFF_ITEMS = [
  { key: 'uniforms',              label: 'Uniforms' },
  { key: 'cleaning',              label: 'Cleaning' },
  { key: 'punctuality',           label: 'Punctuality' },
  { key: 'jobPerformance',        label: 'Job Performance' },
  { key: 'fillingBottleEndOfDay', label: 'Filling Bottle End of Day' },
  { key: 'cleaningSiteAfterWork', label: 'Cleaning Site After Work' },
];
const CLEANING_ITEMS = [
  { key: 'floorClean',               label: 'Floor Clean' },
  { key: 'washpadClean',             label: 'Washpad Clean' },
  { key: 'binsEmpty',                label: 'Bins Empty' },
  { key: 'bottlesLabeled',           label: 'Bottles Labeled' },
  { key: 'highPressureToolsWorking', label: 'High Pressure Tools Working' },
  { key: 'wiresJointsCovered',       label: 'Wires / Joints Covered' },
  { key: 'trolleysClean',            label: 'Trolleys Clean' },
  { key: 'ragsClean',                label: 'Rags Clean' },
  { key: 'usedGlovesInBin',          label: 'Used Gloves in Bin' },
];
const QUALITY_ITEMS = [
  { key: 'carsDeliveredAsPerStandard',     label: 'Cars Delivered as per Standard' },
  { key: 'carsDeliveredOnTime',            label: 'Cars Delivered on Time' },
  { key: 'carsHandledWithCare',            label: 'Cars Handled with Care' },
  { key: 'carDamagePicsRecorded',          label: 'Car Damage Pics Recorded' },
  { key: 'carVideosRecordedBeforeWashing', label: 'Car Videos Recorded Before Washing' },
  { key: 'carsParkedInDesignatedArea',     label: 'Cars Parked in Designated Area' },
  { key: 'carKeysReturnedAsDirected',      label: 'Car Keys Returned as Directed' },
  { key: 'carsWrittenOnSheetWithTimings',  label: 'Cars Written on Sheet with Timings' },
];
const MGR_ITEMS = [
  { key: 'quality',          label: 'Quality' },
  { key: 'cleaning',         label: 'Cleaning' },
  { key: 'sitePresentation', label: 'Site Presentation' },
  { key: 'communication',    label: 'Communication' },
  { key: 'invoicesOnTime',   label: 'Invoices — On Time?' },
  { key: 'staffPunctuality', label: 'Staff Punctuality' },
];

const initTri = (items, data) => Object.fromEntries(items.map(i => [i.key, { rating: data?.[i.key]?.rating || null }]));
const initBin = (items, data) => Object.fromEntries(items.map(i => [i.key, { rating: data?.[i.key]?.rating || null }]));

// ── Toggle buttons ─────────────────────────────────────────
const TriToggle = ({ value, onChange, error }) => (
  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
    {[
      { val: 'satisfactory',   label: '✓ Satisfactory',        ac: 'var(--green)', ab: 'rgba(22,163,74,0.1)',   bc: 'rgba(22,163,74,0.3)'  },
      { val: 'improving',      label: '~ Improvement Required', ac: 'var(--amber)', ab: 'rgba(217,119,6,0.1)',   bc: 'rgba(217,119,6,0.3)'  },
      { val: 'unsatisfactory', label: '✗ Unsatisfactory',       ac: 'var(--red)', ab: 'rgba(220,38,38,0.1)',   bc: 'rgba(220,38,38,0.3)'  },
    ].map(opt => (
      <button key={opt.val} type="button" onClick={() => onChange(opt.val)} style={{
        padding: '5px 10px', borderRadius: '20px', fontSize:'11px', fontWeight: 600,
        cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
        background:   value === opt.val ? opt.ab  : 'var(--bg)',
        borderColor:  value === opt.val ? opt.bc  : error ? 'var(--red)' : 'var(--border-md)',
        border: '1.5px solid',
        color:        value === opt.val ? opt.ac  : 'var(--muted)',
      }}>{opt.label}</button>
    ))}
  </div>
);

const BinToggle = ({ value, onChange, error }) => (
  <div style={{ display: 'flex', gap: '5px' }}>
    {[
      { val: 'yes', label: '✓ Yes', ac: 'var(--green)', ab: 'rgba(22,163,74,0.1)',  bc: 'rgba(22,163,74,0.3)'  },
      { val: 'no',  label: '✗ No',  ac: 'var(--red)', ab: 'rgba(220,38,38,0.1)',  bc: 'rgba(220,38,38,0.3)'  },
    ].map(opt => (
      <button key={opt.val} type="button" onClick={() => onChange(opt.val)} style={{
        padding: '5px 14px', borderRadius: '20px', fontSize: '.74rem', fontWeight: 600,
        cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
        background:  value === opt.val ? opt.ab  : 'var(--bg)',
        borderColor: value === opt.val ? opt.bc  : error ? 'var(--red)' : 'var(--border-md)',
        border: '1.5px solid',
        color:       value === opt.val ? opt.ac  : 'var(--muted)',
      }}>{opt.label}</button>
    ))}
  </div>
);

// ── Section card ───────────────────────────────────────────
const SectionCard = ({ icon, title, subtitle, hasError, children, photoField, photos, onAddPhoto }) => (
  <div style={{
    background: '#ffffff',
    border: `1.5px solid ${hasError ? 'rgba(220,38,38,0.4)' : 'var(--border-md)'}`,
    borderRadius: '14px', overflow: 'hidden', marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,119,168,0.06)',
    transition: 'border-color .2s',
  }}>
    {/* Card header */}
    <div style={{
      background: hasError ? 'rgba(220,38,38,0.04)' : 'var(--bg)',
      borderBottom: `1px solid ${hasError ? 'rgba(220,38,38,0.15)' : 'var(--border-md)'}`,
      padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <div style={{ width: '34px', height: '34px', background: 'var(--blue)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize:'14px', fontWeight:'500', color:'var(--blue)' }}>{title}</div>
        {subtitle && <div style={{ fontSize:'11px', color:'var(--muted)', marginTop: '1px' }}>{subtitle}</div>}
      </div>
      {hasError && <span style={{ background: 'rgba(220,38,38,0.08)', color: 'var(--red)', fontSize: '.68rem', fontWeight:'500', padding: '2px 9px', borderRadius: '20px', border: '1px solid rgba(220,38,38,0.2)' }}>⚠ Incomplete</span>}
    </div>

    {/* Card body */}
    <div style={{ padding: '4px 18px 16px' }}>
      {children}

      {/* Photo upload */}
      <div style={{ marginTop: '14px', borderTop: '1px dashed var(--border-md)', paddingTop: '12px' }}>
        <div style={{ fontSize: '.68rem', fontWeight:'500', textTransform: 'uppercase', letterSpacing: '.8px', color:'var(--muted)', marginBottom: '8px' }}>📸 Photos (optional)</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {photos.map((p, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={p.preview || p.url} alt="" style={{ width: '62px', height: '62px', borderRadius: '8px', objectFit: 'cover', border: '1.5px solid var(--border-md)' }}/>
              <button type="button" onClick={() => onAddPhoto(photoField, null, i)}
                style={{ position: 'absolute', top: '-5px', right: '-5px', width: '18px', height: '18px', background: 'var(--red)', border: 'none', borderRadius: '50%', color: '#fff', fontSize: '.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
          ))}
          <label style={{ width: '62px', height: '62px', borderRadius: '8px', border: '1.5px dashed var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--blue)', fontSize: '1.4rem', background: 'rgba(0,180,216,0.04)', transition: 'all .15s' }}>
            +
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => onAddPhoto(photoField, e.target.files)}/>
          </label>
        </div>
      </div>
    </div>
  </div>
);

const RatingRow = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid var(--bg)', flexWrap: 'wrap', gap: '8px' }}>
    <span style={{ fontSize: '.87rem', fontWeight: 500, color: 'var(--text)', flex: 1, minWidth: '160px' }}>{label}</span>
    {children}
  </div>
);

// ── Main Component ─────────────────────────────────────────
export default function Inspection({ existingData, editMode = false, inspectionId }) {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const locationGroups = useAllLocations();   // CarSaloon sites + inspection locations + dealerships

  const buildInitialForm = (data) => ({
    site:              data?.site              || (user?.site || ''),
    staffPresentation: initTri(STAFF_ITEMS,    data?.staffPresentation),
    siteCleaning:      initBin(CLEANING_ITEMS, data?.siteCleaning),
    quality:           initBin(QUALITY_ITEMS,  data?.quality),
    managerFeedback:   initTri(MGR_ITEMS,      data?.managerFeedback),
    suggestions:       { text: data?.suggestions?.text || '' },
  });

  const [form,        setForm]        = useState(() => buildInitialForm(existingData));
  const [photos,      setPhotos]      = useState({ staffPresentationPhotos:[], siteCleaningPhotos:[], qualityPhotos:[], managerFeedbackPhotos:[], suggestionsPhotos:[] });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [submitted,   setSubmitted]   = useState(false);
  const [error,       setError]       = useState(null);

  useEffect(() => { if (existingData) setForm(buildInitialForm(existingData)); }, [existingData]);

  const now = new Date().toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handlePhoto = (field, files, removeIdx) => {
    if (removeIdx !== undefined && removeIdx !== null) {
      setPhotos(p => ({ ...p, [field]: p[field].filter((_, i) => i !== removeIdx) }));
      return;
    }
    const newPhotos = Array.from(files).map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setPhotos(p => ({ ...p, [field]: [...p[field], ...newPhotos] }));
  };

  const validate = () => {
    const errs = {};
    if (!form.site) errs.site = true;
    const check = (section, items, key) => { if (items.filter(i => section[i.key]?.rating).length === 0) errs[key] = true; };
    check(form.staffPresentation, STAFF_ITEMS,    'staffPresentation');
    check(form.siteCleaning,      CLEANING_ITEMS, 'siteCleaning');
    check(form.quality,           QUALITY_ITEMS,  'quality');
    check(form.managerFeedback,   MGR_ITEMS,      'managerFeedback');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { setError('Please complete all required sections before submitting.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSubmitting(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify({ site: form.site, staffPresentation: form.staffPresentation, siteCleaning: form.siteCleaning, quality: form.quality, managerFeedback: form.managerFeedback, suggestions: form.suggestions }));
      Object.entries(photos).forEach(([field, arr]) => arr.forEach(p => { if (p.file) fd.append(field, p.file); }));
      if (editMode && inspectionId) {
        await api.put(`/inspections/${inspectionId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/inspections', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm(buildInitialForm(null));
    setPhotos({ staffPresentationPhotos:[], siteCleaningPhotos:[], qualityPhotos:[], managerFeedbackPhotos:[], suggestionsPhotos:[] });
    setSubmitted(false); setError(null); setFieldErrors({});
  };

  // ── Success screen ──
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background:'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ width: '80px', height: '80px', background: 'rgba(22,163,74,0.1)', borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem', border: '2px solid rgba(22,163,74,0.3)' }}>✅</div>
          <h2 style={{ color: 'var(--green)', fontSize: '1.4rem', fontWeight:'500', marginBottom: '8px' }}>
            {editMode ? 'Inspection Updated!' : 'Inspection Submitted!'}
          </h2>
          <p style={{ color:'var(--muted)', fontSize: '.88rem', lineHeight: 1.6, marginBottom: '20px' }}>
            {editMode ? 'Your changes have been saved successfully.' : 'Your site inspection has been saved and the owner has been notified.'}
          </p>
          <div style={{ background: '#ffffff', border: '1px solid var(--border-md)', borderRadius: '12px', padding: '16px 20px', textAlign: 'left', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,119,168,0.06)' }}>
            {[['Manager', user?.name], ['Site', form.site], [editMode ? 'Updated' : 'Submitted', now], ['Status', '✅ Recorded']].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '.84rem', borderBottom: '1px solid var(--bg)' }}>
                <span style={{ color:'var(--muted)' }}>{k}</span>
                <span style={{ fontWeight: 600, color: k === 'Status' ? 'var(--green)' : 'var(--text)' }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={() => navigate('/inspections')} style={{ background: '#ffffff', border: '1.5px solid var(--blue)', borderRadius: '9px', padding: '10px 20px', color:'var(--blue)', fontFamily: 'inherit', fontSize: '.88rem', fontWeight: 600, cursor: 'pointer' }}>
              View All
            </button>
            {!editMode && (
              <button onClick={resetForm} style={{ background: 'var(--blue)', border: 'none', borderRadius: '9px', padding: '10px 20px', color: '#fff', fontFamily: 'inherit', fontSize: '.88rem', fontWeight:'500', cursor: 'pointer' }}>
                ➕ New Inspection
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const setTri = (section, key, val) => setForm(f => ({ ...f, [section]: { ...f[section], [key]: { rating: val } } }));
  const setBin = (section, key, val) => setForm(f => ({ ...f, [section]: { ...f[section], [key]: { rating: val } } }));

  return (
    <div style={{ minHeight: '100vh', background:'var(--bg)', fontFamily: "'Inter', sans-serif" }}>

      {/* Header bar */}
      <div style={{ background: '#ffffff', borderBottom: '2px solid var(--blue)', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 2px 8px rgba(0,119,168,0.08)' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'var(--bg)', border: '1px solid var(--border-md)', color:'var(--muted)', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '.8rem', fontFamily: 'inherit' }}>← Back</button>
        <div>
          <div style={{ color:'var(--blue)', fontWeight:'500', fontSize: '1rem' }}>{editMode ? '✏️ Edit Inspection' : '📋 New Inspection'}</div>
          <div style={{ color:'var(--muted)', fontSize:'11px' }}>Car Saloon · {user?.name}</div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize:'11px', color:'var(--muted)', fontFamily: "'JetBrains Mono', monospace" }}>{now}</div>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: '700px', margin: '0 auto', padding: '22px 16px 100px' }}>

        {/* Global error */}
        {error && (
          <div style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '10px', padding: '12px 16px', color: 'var(--red)', fontSize: '.84rem', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Intro banner */}
        {!editMode && (
          <div style={{ background: 'var(--blue)', borderRadius: '14px', padding: '22px', marginBottom: '20px', color: '#fff' }}>
            <div style={{ fontSize:'11px', fontWeight:'500', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px', opacity: .8 }}>🔍 Official Document</div>
            <div style={{ fontSize: '1.2rem', fontWeight:'600', marginBottom: '4px' }}>Car Saloon Site Inspection</div>
            <div style={{ fontSize: '.82rem', opacity: .85 }}>Must be completed with full care. The owner will be notified on submission.</div>
          </div>
        )}

        {/* Inspector Details */}
        <SectionCard icon="👤" title="Inspector Details" subtitle="Auto-filled from your login" photoField="none" photos={[]} onAddPhoto={() => {}}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', paddingTop: '10px' }}>
            {[
              { label: 'Manager Name',  value: user?.name  },
              { label: 'Manager Email', value: user?.email },
            ].map(f => (
              <div key={f.label}>
                <label style={lblStyle}>{f.label}</label>
                <input value={f.value || ''} readOnly style={{ ...inputStyle, background: 'var(--bg)', color:'var(--blue)', fontFamily: "'JetBrains Mono', monospace", fontSize: '.83rem', cursor: 'default' }}/>
              </div>
            ))}
            <div>
              <label style={lblStyle}>Inspection Location *</label>
              <select value={form.site} onChange={e => { setForm(f => ({ ...f, site: e.target.value })); setFieldErrors(fe => ({ ...fe, site: false })); }}
                style={{ ...inputStyle, borderColor: fieldErrors.site ? 'var(--red)' : 'var(--border-md)' }} required>
                <option value="">— Select Site —</option>
                <LocationOptions groups={locationGroups}/>
              </select>
              {fieldErrors.site && <div style={{ color: 'var(--red)', fontSize:'11px', marginTop: '4px' }}>⚠ Please select a site</div>}
            </div>
            <div>
              <label style={lblStyle}>Date & Time</label>
              <input value={now} readOnly style={{ ...inputStyle, background: 'var(--bg)', color:'var(--blue)', fontFamily: "'JetBrains Mono', monospace", fontSize: '.83rem', cursor: 'default' }}/>
            </div>
          </div>
        </SectionCard>

        {/* Staff Presentation */}
        <SectionCard icon="👔" title="Staff Presentation" subtitle="Please check this STRICTLY" hasError={fieldErrors.staffPresentation}
          photoField="staffPresentationPhotos" photos={photos.staffPresentationPhotos} onAddPhoto={handlePhoto}>
          {STAFF_ITEMS.map(item => (
            <RatingRow key={item.key} label={item.label}>
              <TriToggle value={form.staffPresentation[item.key]?.rating} error={fieldErrors.staffPresentation}
                onChange={val => { setTri('staffPresentation', item.key, val); setFieldErrors(fe => ({ ...fe, staffPresentation: false })); }}/>
            </RatingRow>
          ))}
        </SectionCard>

        {/* Site Cleaning */}
        <SectionCard icon="🧹" title="Site Cleaning" subtitle="Please check all boxes" hasError={fieldErrors.siteCleaning}
          photoField="siteCleaningPhotos" photos={photos.siteCleaningPhotos} onAddPhoto={handlePhoto}>
          {CLEANING_ITEMS.map(item => (
            <RatingRow key={item.key} label={item.label}>
              <BinToggle value={form.siteCleaning[item.key]?.rating} error={fieldErrors.siteCleaning}
                onChange={val => { setBin('siteCleaning', item.key, val); setFieldErrors(fe => ({ ...fe, siteCleaning: false })); }}/>
            </RatingRow>
          ))}
        </SectionCard>

        {/* Quality */}
        <SectionCard icon="⭐" title="Quality" subtitle="Check quality report with manager" hasError={fieldErrors.quality}
          photoField="qualityPhotos" photos={photos.qualityPhotos} onAddPhoto={handlePhoto}>
          {QUALITY_ITEMS.map(item => (
            <RatingRow key={item.key} label={item.label}>
              <BinToggle value={form.quality[item.key]?.rating} error={fieldErrors.quality}
                onChange={val => { setBin('quality', item.key, val); setFieldErrors(fe => ({ ...fe, quality: false })); }}/>
            </RatingRow>
          ))}
        </SectionCard>

        {/* Manager Feedback */}
        <SectionCard icon="💬" title="Manager's Feedback" subtitle="Ask manager their response" hasError={fieldErrors.managerFeedback}
          photoField="managerFeedbackPhotos" photos={photos.managerFeedbackPhotos} onAddPhoto={handlePhoto}>
          {MGR_ITEMS.map(item => (
            <RatingRow key={item.key} label={item.label}>
              <TriToggle value={form.managerFeedback[item.key]?.rating} error={fieldErrors.managerFeedback}
                onChange={val => { setTri('managerFeedback', item.key, val); setFieldErrors(fe => ({ ...fe, managerFeedback: false })); }}/>
            </RatingRow>
          ))}
        </SectionCard>

        {/* Suggestions */}
        <SectionCard icon="💡" title="Suggestion or Review" subtitle="Add your or manager's suggestions"
          photoField="suggestionsPhotos" photos={photos.suggestionsPhotos} onAddPhoto={handlePhoto}>
          <textarea value={form.suggestions.text}
            onChange={e => setForm(f => ({ ...f, suggestions: { text: e.target.value } }))}
            placeholder="Write suggestions, observations or feedback..." rows={4}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Inter', sans-serif", marginTop: '10px' }}/>
        </SectionCard>

        {/* Sticky bottom bar */}
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#ffffff', borderTop: '1.5px solid var(--border-md)', padding: '12px 24px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 70px))', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 150, boxShadow: '0 -2px 12px rgba(0,119,168,0.08)' }}>
          <div style={{ fontSize: '.78rem', color:'var(--muted)' }}>
            <span style={{ color:'var(--blue)', fontWeight: 600 }}>{user?.name}</span> · {form.site || 'No site selected'}
            {Object.values(fieldErrors).some(Boolean) && <span style={{ color: 'var(--red)', marginLeft: '10px' }}>⚠ Fix errors above</span>}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={() => navigate(-1)} style={{ background: 'var(--bg)', border: '1px solid var(--border-md)', borderRadius: '9px', padding: '10px 18px', color:'var(--muted)', fontFamily: 'inherit', fontSize: '.85rem', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={submitting} style={{ background: submitting ? 'var(--border-md)' : 'var(--blue)', border: 'none', borderRadius: '10px', padding: '10px 26px', color: '#fff', fontFamily: 'inherit', fontSize: '.9rem', fontWeight:'600', cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: submitting ? 'none' : '0 4px 14px rgba(0,119,168,0.3)', transition: 'all .2s' }}>
              {submitting ? '⏳ Saving...' : editMode ? '💾 Save Changes' : '✅ Submit Inspection'}
            </button>
          </div>
        </div>

      </form>
    </div>
  );
}

const lblStyle   = { display: 'block', fontSize:'11px', fontWeight:'500', textTransform: 'uppercase', letterSpacing: '.8px', color:'var(--muted)', marginBottom: '6px' };
const inputStyle = { width: '100%', background: 'var(--bg)', border: '1.5px solid var(--border-md)', borderRadius: '8px', padding: '9px 13px', color: 'var(--text)', fontFamily: "'Inter', sans-serif", fontSize: '.88rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color .2s' };
